/**
 * Courses Overview Page
 *
 * Displays all available CPR Training Direct courses in a grid layout.
 * Courses are sorted by order and clicking a course navigates to its detail page.
 * BRUTALIST STREETWEAR DESIGN SYSTEM
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CourseGrid from '../../components/cpr/CourseGrid';
import { getAllCourses } from '../../data/courses';
import { Course } from '../../types/course';
import { Card } from '../../components/Card';

const CoursesOverview: React.FC = () => {
  const navigate = useNavigate();
  const courses = getAllCourses();

  const handleCourseClick = (course: Course) => {
    navigate(`/courses/${course.slug}`);
  };

  // Generate ItemList Schema for structured data
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": courses.map((course, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Course",
        "@id": `https://cprtrainingdirect.com/courses/${course.slug}`,
        "name": course.name,
        "description": course.shortDescription,
        "url": `https://cprtrainingdirect.com/courses/${course.slug}`,
        "provider": {
          "@type": "Organization",
          "name": "CPR Training Direct"
        }
      }
    }))
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>CPR Certification Courses | CPR Training Direct Orlando & Atlanta</title>
        <meta name="title" content="CPR Certification Courses | CPR Training Direct Orlando & Atlanta" />
        <meta
          name="description"
          content="Choose from CPR/AED, BLS for Healthcare, Renewal, or Group Training. American Heart Association certified courses with same-day certification in Orlando and Atlanta."
        />
        <meta
          name="keywords"
          content="CPR courses, AED training, BLS certification, CPR renewal, group training, healthcare CPR"
        />
        <meta name="author" content="CPR Training Direct" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cprtrainingdirect.com/courses" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cprtrainingdirect.com/courses" />
        <meta property="og:title" content="CPR Certification Courses | CPR Training Direct Orlando & Atlanta" />
        <meta
          property="og:description"
          content="Choose from CPR/AED, BLS for Healthcare, Renewal, or Group Training. American Heart Association certified courses with same-day certification in Orlando and Atlanta."
        />
        <meta property="og:image" content="https://cprtrainingdirect.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="CPR Training Direct" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cprtrainingdirect.com/courses" />
        <meta name="twitter:title" content="CPR Certification Courses | CPR Training Direct Orlando & Atlanta" />
        <meta
          name="twitter:description"
          content="Choose from CPR/AED, BLS for Healthcare, Renewal, or Group Training. American Heart Association certified courses with same-day certification in Orlando and Atlanta."
        />
        <meta name="twitter:image" content="https://cprtrainingdirect.com/og-image.jpg" />

        {/* Geo Tags for Local SEO */}
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Orlando" />
        <meta name="geo.position" content="28.5383;-81.3792" />
        <meta name="ICBM" content="28.5383, -81.3792" />

        {/* Schema.org Structured Data - ItemList */}
        <script type="application/ld+json">
          {JSON.stringify(itemListSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header Section - Brutalist Hero */}
      <div className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-5xl mx-auto">
            <h1
              data-aos="fade-up"
              className="font-display text-6xl md:text-7xl lg:text-8xl font-bold uppercase tracking-wider text-indigo_dye-500 mb-6"
            >
              Training Courses
            </h1>
            <p
              data-aos="fade-up"
              data-aos-delay="100"
              className="font-body text-lg md:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto"
            >
              Choose from our comprehensive selection of American Heart Association certified
              CPR and life-saving courses. All courses include same-day certification and
              hands-on training with experienced instructors.
            </p>
          </div>
        </div>
      </div>

      {/* Courses Grid Section */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <CourseGrid
          courses={courses}
          onCourseClick={handleCourseClick}
        />
      </div>

      {/* Additional Info Section - Brutalist Stats Cards */}
      <div className="bg-white py-16 md:py-24 mt-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div data-aos="zoom-in" data-aos-delay="0">
              <Card variant="brutalist" className="text-center h-full">
                <div className="font-display text-5xl md:text-6xl font-extrabold text-amaranth_purple-500 mb-4">
                  2 YEARS
                </div>
                <p className="font-body text-lg text-gray-700 uppercase tracking-wide font-semibold">
                  Certification Validity
                </p>
              </Card>
            </div>
            <div data-aos="zoom-in" data-aos-delay="100">
              <Card variant="brutalist" className="text-center h-full">
                <div className="font-display text-5xl md:text-6xl font-extrabold text-amaranth_purple-500 mb-4">
                  SAME DAY
                </div>
                <p className="font-body text-lg text-gray-700 uppercase tracking-wide font-semibold">
                  Digital Certification
                </p>
              </Card>
            </div>
            <div data-aos="zoom-in" data-aos-delay="200">
              <Card variant="brutalist" className="text-center h-full">
                <div className="font-display text-5xl md:text-6xl font-extrabold text-amaranth_purple-500 mb-4">
                  AHA
                </div>
                <p className="font-body text-lg text-gray-700 uppercase tracking-wide font-semibold">
                  Certified Programs
                </p>
              </Card>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default CoursesOverview;
